/**
 * nativeApi — read-only client for the native ViewIR framework endpoints.
 *
 *   GET /api/widget-native/registry               → registry.json (L0…L4 components)
 *   GET /api/widget-native/blueprints             → [{ id, title, widget_type, … }]
 *   GET /api/widget-native/blueprints/{id}?source=repo → the in-repo <id>.view.json
 *
 * The studio is the AUTHORING surface: it edits the in-repo SSOT, so its
 * blueprint read is pinned to `?source=repo` — the repo working copy, including
 * unpublished edits. (Without the param the route is the RUNTIME read and
 * returns the widget's PINNED published native version, which is what the app
 * renders, not what you're editing.) Registry + the list stay repo-served.
 * We go through `api.authFetch` so the call carries the same headers as every
 * other playground fetch and works behind the dev proxy.
 *
 * Both the registry and each blueprint are immutable for the lifetime of a
 * page load, so results are memoised in-module — the bootstrap browser, the
 * preview pane and the signal/slot graph all read the same parse.
 */
import { api } from '../../services/api';

const NATIVE_BASE = '/api/widget-native';

// The a2ui widget-catalog capability header (DEC-4). A 0.10.0-capable client
// advertises it on every widget-native GET so the server serves a2ui (`view_ir:2`)
// rows where a version exists; 0.9.x / stale builds omit it and get v1 rows. The
// web renderer handles both majors (NativeWidgetRuntime branches on `view_ir`).
const A2UI_CATALOGS_HEADER = { 'a2ui-catalogs': 'native-ir-widgets' };

// path → Promise<json>. A rejected entry is dropped so a retry can re-fetch.
const _cache = new Map();

async function getJson(path) {
  if (_cache.has(path)) return _cache.get(path);
  const p = (async () => {
    const resp = await api.authFetch(path, { headers: A2UI_CATALOGS_HEADER });
    // X-Blueprint-Format ("a2ui" | "viewir") says which major the server served;
    // the blueprint's own `view_ir` is authoritative for the runtime, so this is
    // informational (surfaced for the studio/doctor).
    if (!resp.ok) {
      let detail = '';
      try {
        const body = await resp.json();
        detail = body?.detail ? ` — ${body.detail}` : '';
      } catch { /* non-JSON error body */ }
      throw new Error(`${path} failed: ${resp.status}${detail}`);
    }
    return resp.json();
  })();
  _cache.set(path, p);
  p.catch(() => _cache.delete(path));
  return p;
}

export const nativeApi = {
  /** The component registry (layers, tokens, components[]). */
  registry() {
    return getJson(`${NATIVE_BASE}/registry`);
  },

  /** List authored blueprints: `{ blueprints: [{ id, title, widget_type }] }`. */
  listBlueprints() {
    return getJson(`${NATIVE_BASE}/blueprints`);
  },

  /** One widget's ViewIR blueprint — the repo working copy the studio authors. */
  blueprint(widgetId) {
    return getJson(`${NATIVE_BASE}/blueprints/${encodeURIComponent(widgetId)}?source=repo`);
  },
};

export default nativeApi;
